import { Card, ContestParticipant, Booster } from 'models';

export type CardPlaceholderData = {
    key: string;
    type: 'start' | 'bench' | 'booster';
    card?: Card;
    booster?: Booster;
    noName?: boolean;
};

/**
 * Generates an array of card placeholder data for rendering in contest lists
 * This function handles the logic for determining what cards/placeholders to show
 * and returns a simple array that can be mapped over for rendering
 */
export const generateCardPlaceholderData = (participation?: ContestParticipant | null): CardPlaceholderData[] => {
    if (!participation) {
        // Show empty placeholders when no participation
        return [
            { key: 'missing-start-0', type: 'start', noName: true },
            { key: 'missing-start-1', type: 'start', noName: true },
            { key: 'missing-start-2', type: 'start', noName: true },
            { key: 'missing-bench', type: 'bench', noName: true },
            { key: 'missing-booster', type: 'booster', noName: true },
        ];
    }

    const placeholders: CardPlaceholderData[] = [];

    // Handle start cards (always show 3, fill with placeholders if needed)
    const startCards = participation.cards.cardsWithStatus.filter((card) => card.status === 'start') || [];

    // Add existing start cards
    startCards.forEach((card) => {
        placeholders.push({
            key: `start-${card.id}`,
            type: 'start',
            card,
        });
    });

    // Add missing start card placeholders
    const missingStartCards = 3 - startCards.length;
    for (let i = 0; i < missingStartCards; i++) {
        placeholders.push({
            key: `missing-start-${i}`,
            type: 'start',
            noName: true,
        });
    }

    // Handle bench card (always show 1)
    const benchCard = participation.cards.cardsWithStatus.find((card) => card.status === 'bench');

    if (benchCard) {
        placeholders.push({
            key: `bench-${benchCard.id}`,
            type: 'bench',
            card: benchCard,
        });
    } else {
        placeholders.push({
            key: 'missing-bench',
            type: 'bench',
            noName: true,
        });
    }

    // Handle booster (always show 1)
    if (participation.booster) {
        placeholders.push({
            key: `booster-${participation.booster.id}`,
            type: 'booster',
            booster: participation.booster,
        });
    } else {
        placeholders.push({
            key: 'missing-booster',
            type: 'booster',
            noName: true,
        });
    }

    return placeholders;
};
